import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import CustomSelect from '../../../ui/CustomSelect';

const statusOptions = [
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Tidak Aktif' },
];

export default function PositionModal({
  open,
  data,
  departments,
  subDivisions,
  roles,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(data || {});
  const [errors, setErrors] = useState({
    code: '',
    name: '',
    department_id: '',
  });

  useEffect(() => {
    setForm(data || {});
    // Reset errors when modal opens/closes
    setErrors({ code: '', name: '', department_id: '' });
  }, [data, open]);

  if (!open) return null;

  const handleSubmit = e => {
    e.preventDefault();

    // Reset errors
    const newErrors = { code: '', name: '', department_id: '' };
    let hasError = false;

    // Validate required fields
    if (!form?.code || form.code.trim() === '') {
      newErrors.code = 'Kode wajib diisi';
      hasError = true;
    }

    if (!form?.name || form.name.trim() === '') {
      newErrors.name = 'Nama Jabatan wajib diisi';
      hasError = true;
    }

    if (!form?.department_id || form.department_id === '') {
      newErrors.department_id = 'Divisi wajib dipilih';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({ code: '', name: '', department_id: '' });

    // Helper function to convert empty string to null or integer
    const toIntOrNull = value => {
      if (value === '' || value === null || value === undefined) return null;
      const num = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(num) ? null : num;
    };

    onSave({
      ...form,
      level: toIntOrNull(form.level),
      department_id: toIntOrNull(form.department_id),
      sub_division_id: toIntOrNull(form.sub_division_id),
      role_id: toIntOrNull(form.role_id),
      salary_grade_id: toIntOrNull(form.salary_grade_id),
      credit_approval_limit: toIntOrNull(form.credit_approval_limit),
      job_weight: toIntOrNull(form.job_weight),
    });
  };

  const filteredSubDivisions = subDivisions.filter(s => s.department_id === form?.department_id);

  const deptOptions = [
    { value: '', label: 'Pilih divisi' },
    ...departments.map(d => ({ value: d.id, label: d.name })),
  ];

  const subDivOptions = [
    { value: '', label: 'Pilih sub divisi' },
    ...filteredSubDivisions.map(s => ({ value: s.id, label: s.name })),
  ];

  const roleOptions = [
    { value: '', label: 'Pilih role' },
    ...(roles || []).map(r => ({ value: r.id, label: r.name })),
  ];

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden animate-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{form?.id ? 'Edit' : 'Tambah'} Jabatan</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <h4 className="font-semibold">Informasi Dasar</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Kode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.code || ''}
                onChange={e => {
                  setForm({ ...form, code: e.target.value });
                  // Clear error when user starts typing
                  if (errors.code) {
                    setErrors({ ...errors, code: '' });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.code
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-emerald-500'
                }`}
                required
              />
              {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Nama Jabatan <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.name || ''}
                onChange={e => {
                  setForm({ ...form, name: e.target.value });
                  // Clear error when user starts typing
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-emerald-500'
                }`}
                required
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Level</label>
              <input
                type="number"
                value={form?.level || ''}
                onChange={e => setForm({ ...form, level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Divisi <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={form?.department_id || ''}
                onChange={val => {
                  setForm({ ...form, department_id: val, sub_division_id: '' });
                  // Clear error when user selects
                  if (errors.department_id) {
                    setErrors({ ...errors, department_id: '' });
                  }
                }}
                options={deptOptions}
                placeholder="Pilih divisi"
                width="w-full"
                error={!!errors.department_id}
              />
              {errors.department_id && (
                <p className="text-xs text-red-500 mt-1">{errors.department_id}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Sub Divisi</label>
              <CustomSelect
                value={form?.sub_division_id || ''}
                onChange={val => setForm({ ...form, sub_division_id: val })}
                options={subDivOptions}
                placeholder="Pilih sub divisi"
                width="w-full"
                disabled={!form?.department_id}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Role</label>
              <CustomSelect
                value={form?.role_id || ''}
                onChange={val => setForm({ ...form, role_id: val })}
                options={roleOptions}
                placeholder="Pilih role"
                width="w-full"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <CustomSelect
                value={form?.status || 'active'}
                onChange={val => setForm({ ...form, status: val })}
                options={statusOptions}
                placeholder="Pilih status"
                width="w-full"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Deskripsi</label>
            <textarea
              value={form?.description || ''}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 font-medium"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
